import { Language, Menu as MenuIcon, Telegram, Twitter } from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { loadLinksPreset } from "@tsparticles/preset-links";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Link as NavLink, useLocation, useNavigate } from "react-router-dom";
import { TypeAnimation } from 'react-type-animation';
import ChatButton from "../components/ChatButton";
import { useWallet } from "../context/WalletContext";
import { platforms } from "../shared/platforms";
import { axiosGet } from "../shared/requests";
// @ts-ignore

const navLinks = [
  { to: "/", text: "Home" }
];

interface LayoutProps {
  children: ReactNode;
}

const CustomConnectButton = (props: { onlyText: boolean }) => {
  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        const ready = mounted && authenticationStatus !== "loading";
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === "authenticated");

        if (props.onlyText) {
          return (
            <>
              <Typography
                variant="h1"
                sx={{
                  fontSize: "2rem",
                  fontWeight: "bold",
                  cursor: "pointer"
                }}
                onClick={openConnectModal}
              >
                Connect your wallet!
              </Typography>
            </>
          );
        }

        return (
          <div
            {...(!ready && {
              "aria-hidden": true,
              style: {
                opacity: 0,
                pointerEvents: "none",
                userSelect: "none",
              },
            })}>
            {(() => {
              if (!connected) {
                return (
                  <Button
                    variant="contained"
                    sx={{ marginLeft: 2 }}
                    onClick={openConnectModal}
                    type="button">
                    Connect Wallet
                  </Button>
                );
              }
              if (chain.unsupported) {
                return (
                  <Button
                    variant="contained"
                    sx={{ marginLeft: 2 }}
                    onClick={openChainModal}
                    type="button">
                    Change network
                  </Button>
                );
              }
              return (
                <Button
                  variant="contained"
                  onClick={openAccountModal}
                  sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: 2, padding: '8px 16px' }}
                >
                  {chain.iconUrl && (
                    <Avatar
                      src={chain.iconUrl}
                      alt={chain.name}
                      sx={{ width: 24, height: 24 }}
                    />
                  )}
                  {account.displayName}
                  {account.displayBalance ? ` (${account.displayBalance})` : ""}
                </Button>
              );
            })()}
          </div>
        );
      }}
    </ConnectButton.Custom>
  );
};

const DegenMode = (props: { degenMode: boolean, setDegenMode: (value: boolean) => void }) => {
  return (
    <Button
      variant="contained"
      sx={{
        marginLeft: 2,
        backgroundColor: "#39FF14",
        color: "black",
        padding: "8px 16px",
        "&:hover": { backgroundColor: "#2E7D32" },
        textDecoration: props.degenMode ? "none" : "line-through",
      }}
      onClick={() => {
        props.setDegenMode(!props.degenMode);
        localStorage.setItem("degenMode", (!props.degenMode) ? '1' : '0'); // Save to local storage
      }}
    >
      Degen Mode
    </Button>
  );
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [degentMode, setDegenMode] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { account, connected } = useWallet();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const isSmallScreen = useMediaQuery("(max-width:600px)"); // Media query for small screens
  const myRef = useRef<HTMLDivElement | null>(null)
  const [vantaEffect, setVantaEffect] = useState<any>(null)

  const playMenuClick = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const isActiveLink = (link: string) => location.pathname === link;

  useEffect(() => {
    if (account) {
      const fetchUnreadMessagesCount = async () => {
        try {
          const response = await axiosGet("api/chats/unread-messages-count", account);
          console.log("unread-messages-count", response);
          setUnreadCount(response.unreadCount);
        } catch (error) {
          console.error("Error fetching unread message count:", error);
        }
      };
      const intervalId = setInterval(fetchUnreadMessagesCount, 5000);
      fetchUnreadMessagesCount();

      return () => clearInterval(intervalId);
    }
  }, [account]);

  // useEffect(() => {
  //   if (!vantaEffect) {
  //     setVantaEffect(
  //       VANTA({
  //         el: myRef.current,
  //         THREE: THREE, // Pass the imported THREE object
  //         mouseControls: true,
  //         touchControls: true,
  //         gyroControls: false,
  //         minHeight: 200.00,
  //         minWidth: 200.00,
  //         scale: 1.00,
  //         scaleMobile: 1.00,
  //         color: 0x82ff3f,
  //         backgroundColor: 0xd0b11,
  //         points: 19.00,
  //         maxDistance: 14.00,
  //         spacing: 19.00
  //       }))
  //   }
  //   return () => {
  //     if (vantaEffect) vantaEffect.destroy()
  //   }
  // }, [vantaEffect])

  const [init, setInit] = useState(false);
  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadLinksPreset(engine);
    }).then(() => {
      setInit(true);
    });
    const storedValue = localStorage.getItem("degenMode");
    if (storedValue !== null) {
      setDegenMode(storedValue == '1');
    }
  }, []);

  const drawerLinks = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <List>
        {navLinks.map((x, i) => (
          <NavLink to={x.to} key={i} onClick={playMenuClick}>
            <ListItem
              sx={{
                backgroundColor: isActiveLink(x.to) ? "#444" : "transparent",
                "&:hover": {
                  backgroundColor: "#555",
                },
              }}
              onClick={toggleDrawer(false)}>
              <ListItemText primary={x.text} sx={{ color: "white" }} />
            </ListItem>
          </NavLink>
        ))}
      </List>
      <Box sx={{ marginTop: "auto" }}>
        <Box sx={{ marginBottom: '5px' }}>
          <DegenMode degenMode={degentMode} setDegenMode={setDegenMode} />
        </Box>
        <CustomConnectButton onlyText={false} />
      </Box>
    </Box>
  );

  return (
    <>
      {degentMode && <div className="scanlines"></div>}
      <div
        className="wrapper"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          backgroundColor: "black",
          color: "#39FF14",
        }}>
        <audio ref={audioRef} src="/menu_click.mpeg" />
        <AppBar
          position="fixed"
          sx={{ backgroundColor: "black", color: "#39FF14" }}>
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <div>
              <IconButton onClick={() => {
                playMenuClick();
                navigate("/")
              }}>
                {/* <img
                  src={platforms[0].logo}
                  alt="Logo"
                  style={{
                    width: isSmallScreen ? "50px" : "100px", // Adjust size for small screens
                    height: isSmallScreen ? "50px" : "100px",
                    borderRadius: "50%",
                    marginRight: "8px",
                  }}
                /> */}
                <img
                  src={platforms[0].networkLogo}
                  alt="Network Logo"
                  style={{
                    width: isSmallScreen ? "50px" : "100px", // Adjust size for small screens
                    height: isSmallScreen ? "50px" : "100px",
                    borderRadius: "50%",
                    marginRight: "8px",
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{ color: "#39FF14", fontWeight: "bold", fontSize: "1rem" }}>
                  {platforms[0].name}
                </Typography>
              </IconButton>
            </div>

            <div className="desktopNavbar">
              {navLinks.map((x, i) => (
                <NavLink to={x.to} key={i} onClick={playMenuClick}>
                  <Link
                    sx={{
                      marginLeft: 2,
                      textDecoration: "none",
                      color: "#39FF14",
                      backgroundColor: isActiveLink(x.to)
                        ? "#444"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "#555",
                      },
                      padding: "8px 16px",
                      borderRadius: "4px",
                      border: "4px dashed",
                    }}>
                    {x.text}
                  </Link>
                </NavLink>
              ))}
              <DegenMode degenMode={degentMode} setDegenMode={setDegenMode} />
              <CustomConnectButton onlyText={false} />
            </div>

            <div className="mobileNavbar">
              <IconButton edge="end" color="inherit" onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>
            </div>
          </Toolbar>
        </AppBar>

        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
          <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}>
            {drawerLinks}
          </Box>
        </Drawer>

        {init && <Particles
          id="tsparticles"
          options={{
            fullScreen: { enable: true }, // Enable full-screen background
            detectRetina: true,
            background: {
              color: "#0d0b11", // Background color
            },
            particles: {
              number: {
                value: 50, // Increase particle count for better visuals
              },
              color: {
                value: "#82ff3f", // Particle color
              },
              shape: {
                type: "circle", // Particle shape
              },
              opacity: {
                value: 0.9, // Slight transparency
              },
              size: {
                value: 3, // Adjust particle size
              },
              move: {
                enable: true,
                speed: 1, // Movement speed
                direction: "none",
                random: false,
                straight: false,
                outModes: {
                  default: "bounce", // Prevents particles from disappearing
                },
              },
              links: {
                enable: true, // **Enable links between particles**
                distance: 100, // **Set max connection distance**
                color: "#82ff3f",
                opacity: 0.8,
                width: 1.5,
              },
            },
            interactivity: {
              events: {
                onHover: {
                  enable: true,
                  mode: "grab", // **Particles link on hover**
                },
                onClick: {
                  enable: true,
                  mode: "push",
                },
              },
              modes: {
                grab: {
                  distance: 140, // **Extend grab effect distance**
                  links: {
                    opacity: 1,
                  },
                },
                push: {
                  quantity: 5, // **More particles on click**
                },
              },
            },
            fpsLimit: 60, // Smooth performance
          }}
          // options={{

          //   particles: {
          //     color: {
          //       value: "0x82ff3f"
          //     },
          //     shape: {
          //       type: "square",
          //     },

          //   },
          //   preset: "links",
          //   background: {
          //     color: "0xd0b11"
          //   },
          //   detectRetina: true
          // }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: -1,
          }}
        />}
        <div style={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh", // Ensures full height
            }}
          >
            <Box
              id="main-component"
              ref={myRef}
              component="main"
              sx={{
                flex: 1,
                marginTop: "90px",
                paddingBottom: "120px",
                position: "relative", // Ensure relative positioning
                height: "auto",
              }}>
              <Box
                sx={{
                  width: "100%",
                  padding: '5px',
                  display: "flex",
                  flexDirection: "column",
                }}>
                {connected ? (
                  children
                ) : (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        // justifyContent: "center",
                        alignItems: "center",
                        padding: 2,
                        textAlign: "center",
                        marginTop: '100px'
                      }}
                    >
                      {/* <Typography variant="body1" sx={{ marginBottom: 2, maxWidth: "600px" }}>
                        <TypeAnimation
                          sequence={[
                            "The Decentralized Parlay Platform (DPP) is the next evolution in betting—where your wagers aren’t just bets, they’re assets.",
                            2000,
                            "Powered by DeFAI (Decentralized Finance Artificial Intelligence), DPP eliminates middlemen, locks in fair odds with AI-driven automation, and turns every parlay into a tradable NFT.",
                            2000,
                            "Bet, sell, or hedge your position mid-game on OverTheCounter.meme...",
                            2000,
                            "Let the game begin!",
                          ]}
                          wrapper="span"
                          speed={10}
                          style={{ fontSize: '1em', display: 'inline-block' }}
                          repeat={Infinity}
                        />
                      </Typography> */}
                      {!connected && (
                        <CustomConnectButton onlyText={true} />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
            <Box
              component="footer"
              sx={{
                textAlign: "center",
                backgroundColor: "black",
                color: "#39FF14",
              }}>
              <Box sx={{ display: "flex", justifyContent: "center", marginTop: 1 }}>
                <NavLink to="https://smooviephone.com/">
                  <Language sx={{ margin: 1, color: "#39FF14" }} />
                </NavLink>
                <NavLink to="https://x.com/smooviephone?s=20">
                  <Telegram sx={{ margin: 1, color: "#39FF14" }} />
                </NavLink>
                <NavLink to="https://x.com/smooviephone?s=20">
                  <Twitter sx={{ margin: 1, color: "#39FF14" }} />
                </NavLink>
              </Box>
              <Typography variant="body2">
                © {new Date().getFullYear()} SmooviePhone. All rights reserved.
              </Typography>
            </Box>
          </Box>

        </div>



        <ChatButton unreadCount={unreadCount} />


      </div >
    </>
  );
};

export default Layout;
